import { useState } from 'react'

import { Box, Button, Link, Text } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { endOfYear, format, startOfYear } from 'date-fns'
import { EmployeeTimePeriodDetailsFragment } from 'generated/resources/graphql'
import { TimePeriodDetailsQueryVariables } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Callout from 'modules/core/components/Callout'
import { DateNavigator } from 'modules/core/components/DateNavigator'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import helpCenterUrl from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import ExportYearlyBalanceModal from 'modules/shift_management/components/ShiftManagement/ExportYearlyBalanceModal'
import { usePermissions } from 'modules/shift_management/hooks/usePermissions'

import AnnualBalance from './AnnualBalance'
import ConvertedHoursToggle from './ConvertedHoursToggle'
import PlannedTime from './PlannedTime'
import { RangeOptionsEnum, ZERO_HOURS, findRangeType } from './utils'

interface Props {
  timePeriodDetails: EmployeeTimePeriodDetailsFragment[]
  locationsIds: string[]
  teamsIds: string[]
  refetch: (variables: TimePeriodDetailsQueryVariables) => void
  loading: boolean
}

type Range = [Date, Date]

const Content = ({ timePeriodDetails, locationsIds, teamsIds, refetch, loading }: Props) => {
  const [{ canSeeTimeBalanceExport }] = usePermissions()

  const [isExportYearlyBalanceModalOpen, setIsExportYearlyBalanceModalOpen] = useState(false)
  const [convertHoursInsight, setConvertHoursInsight] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.shiftManagementConvertHoursInsights,
    false
  )

  const [showMaxYearlyHoursInfo, setShowMaxYearlyHoursInfo] = useState<boolean>(true)
  const [stopShowingMaxYearlyHoursInfo, setStopShowingMaxYearlyHoursInfo] =
    useLocalStorage<boolean>(LOCAL_STORAGE_KEYS.shiftManagementHideInsightsMaxYearlyHours, false)

  const [convertHours, setConvertHours] = useState<boolean>(convertHoursInsight ?? false)

  const dateFrom = startOfYear(new Date())
  const dateTo = endOfYear(new Date())
  const [range, setRange] = useState<Range>([dateFrom, dateTo])
  const [rangeType, setRangeType] = useState<string>(RangeOptionsEnum.year)
  const { navigation } = useNavigation()

  const title = i18n.t('shift_management.time_insights.time_balance_content.title')
  const description = i18n.t('shift_management.time_insights.time_balance_content.description')
  const learnMore = i18n.t('shift_management.time_insights.time_balance_content.learn_more')

  const contractCalloutTitle = i18n.t(
    'shift_management.time_insights.time_balance_content.contract_callout.title'
  )
  const contractCalloutDescription = i18n.t(
    'shift_management.time_insights.time_balance_content.contract_callout.description'
  )
  const contractCalloutButton = i18n.t(
    'shift_management.time_insights.time_balance_content.contract_callout.button'
  )

  const setRangeDetails = (newRange: Range) => {
    setRange(newRange)
    const newRangeType = findRangeType(newRange[0], newRange[1])
    track(trackingEvents.shiftManagement.selectRangeTypeTimeInsights, {
      range_type: newRangeType,
    })
    setRangeType(newRangeType)
  }

  const formattedStart = format(range[0], 'yyyy-MM-dd')
  const formattedEnd = format(range[1], 'yyyy-MM-dd')

  const refreshTimeBalanceDetails = useUnsafeCallback(async () => {
    await refetch({
      ids: [],
      dateFrom: formattedStart,
      dateTo: formattedEnd,
      locationsIds: locationsIds,
      teamsIds: teamsIds,
    })
  }, [refetch, formattedStart, formattedEnd])

  useUnsafeEffect(() => {
    refreshTimeBalanceDetails()
  }, [range]) // eslint-disable-line react-hooks/exhaustive-deps

  const onExportYearlyBalanceAction = () => {
    setIsExportYearlyBalanceModalOpen(true)

    track(trackingEvents.shiftManagement.requestYearlyExportStart, {
      export_from: 'shift_sidepanel',
    })
  }

  const missingMaxHours = timePeriodDetails.some((item) => item.recommendedHours === ZERO_HOURS)
  const showCalloutMissingHours =
    missingMaxHours && showMaxYearlyHoursInfo && !stopShowingMaxYearlyHoursInfo

  return (
    <>
      <Box gap="s32">
        <Box flexDirection="row" gap="s24" justifyContent="spaceBetween">
          <Box gap="s8">
            <Text.Header1 weight="semibold">{title}</Text.Header1>
            <Text.Normal>{description}</Text.Normal>
            <Box maxWidth="fit-content">
              <Link.Href
                href={helpCenterUrl('how_to_view_and_export_the_annual_balance_report')}
                target="_blank"
              >
                {learnMore}
              </Link.Href>
            </Box>
          </Box>
          {canSeeTimeBalanceExport && (
            <Button type="secondary" onClick={onExportYearlyBalanceAction} iconName="download" />
          )}
        </Box>
        <Box flexDirection="row" gap="s8" justifyContent="spaceBetween" alignItems="center">
          <DateNavigator
            regularPeriods={['week', 'month', 'year', 'year_to_date']}
            startDate={range[0]}
            endDate={range[1]}
            onDateRangeChange={(...newRange) => setRangeDetails(newRange)}
          />
          <ConvertedHoursToggle
            convertHours={convertHours}
            setConvertHours={setConvertHours}
            setConvertHoursInsight={setConvertHoursInsight}
          />
        </Box>
        <PlannedTime
          timePeriodDetails={timePeriodDetails}
          rangeType={rangeType}
          loading={loading}
          convertHours={convertHours}
        />
        {showCalloutMissingHours && (
          <Callout
            title={contractCalloutTitle}
            description={contractCalloutDescription}
            type="info"
            onRemove={() => setShowMaxYearlyHoursInfo(false)}
            action={
              <Box gap="s8" flexDirection="row">
                <Button.Router to={navigation.employees.fullPath()} type="primary">
                  {contractCalloutButton}
                </Button.Router>
                <Button type="secondary" onClick={() => setStopShowingMaxYearlyHoursInfo(true)}>
                  {i18n.t('shift_management.actions.got_it_button')}
                </Button>
              </Box>
            }
          />
        )}

        <AnnualBalance
          timePeriodDetails={timePeriodDetails}
          loading={loading}
          convertHours={convertHours}
        />
      </Box>

      {isExportYearlyBalanceModalOpen && (
        <ExportYearlyBalanceModal
          onClose={() => setIsExportYearlyBalanceModalOpen(false)}
          from="shift-sidepanel"
        />
      )}
    </>
  )
}

export default observer(Content)
